import createApiClient from './apiClient';
import { cachedRequest, invalidateStudentCache } from '../utils/apiDataCache';

const studentApi = createApiClient(process.env.REACT_APP_ATHENIAN_STUDENT_SERVICE_URL);

const baseUrl = (process.env.REACT_APP_ATHENIAN_STUDENT_SERVICE_URL || '').replace(/\/api\/students\/?$/, '');
const templateApi = createApiClient(baseUrl);

// Template Management (admin)

export const getTemplates = (includeInactive = false, page = 1, limit = 10) =>
  cachedRequest(
    `eligibility-templates:${includeInactive}:${page}:${limit}`,
    () => templateApi.get('/api/eligibility-templates/', {
      params: { include_inactive: includeInactive, page, limit },
    }),
    300_000,
  );

export const getTemplate = (templateId) =>
  templateApi.get(`/api/eligibility-templates/${templateId}`);

export const createTemplate = (data) =>
  templateApi.post('/api/eligibility-templates/', data);

export const updateTemplate = (templateId, data) =>
  templateApi.put(`/api/eligibility-templates/${templateId}`, data);

export const deleteTemplate = (templateId, permanent = false) =>
  templateApi.delete(`/api/eligibility-templates/${templateId}`, { params: permanent ? { permanent: true } : {} });

export const duplicateTemplate = (templateId) =>
  templateApi.post(`/api/eligibility-templates/${templateId}/duplicate`);

export const addSection = (templateId, data) =>
  templateApi.post(`/api/eligibility-templates/${templateId}/sections`, data);

export const editSection = (templateId, sectionId, data) =>
  templateApi.put(`/api/eligibility-templates/${templateId}/sections/${sectionId}`, data);

export const deleteSection = (templateId, sectionId) =>
  templateApi.delete(`/api/eligibility-templates/${templateId}/sections/${sectionId}`);

export const addField = (templateId, sectionId, data) =>
  templateApi.post(`/api/eligibility-templates/${templateId}/sections/${sectionId}/fields`, data);

export const editField = (templateId, sectionId, fieldId, data) =>
  templateApi.put(`/api/eligibility-templates/${templateId}/sections/${sectionId}/fields/${fieldId}`, data);

export const deleteField = (templateId, sectionId, fieldId) =>
  templateApi.delete(`/api/eligibility-templates/${templateId}/sections/${sectionId}/fields/${fieldId}`);

// Student Eligibility Checklist

export const getEligibilityChecklist = (studentId, checklistId, disabilityCategory) => {
  const params = {};
  if (checklistId) params.checklist_id = checklistId;
  if (disabilityCategory) params.disability_category = disabilityCategory;
  const cacheKey = `student:${studentId}:eligibility:${checklistId || 'latest'}:${disabilityCategory || 'none'}`;
  return cachedRequest(cacheKey, () =>
    studentApi.get(`/${studentId}/eligibility-checklist`, { params }));
};

export const listEligibilityChecklists = (studentId) =>
  cachedRequest(`student:${studentId}:eligibility-list`, () =>
    studentApi.get(`/${studentId}/eligibility-checklist/list`));

export const createEligibilityChecklist = (studentId, data) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/eligibility-checklist`, data);
};

export const updateResponses = (studentId, checklistId, responses) => {
  invalidateStudentCache(studentId);
  return studentApi.put(`/${studentId}/eligibility-checklist/${checklistId}/responses`, { responses });
};

export const updateHeader = (studentId, checklistId, header) => {
  invalidateStudentCache(studentId);
  return studentApi.put(`/${studentId}/eligibility-checklist/${checklistId}/header`, { header });
};

export const syncChecklist = (studentId, checklistId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/eligibility-checklist/${checklistId}/sync`);
};

export const evaluateFromDocs = (studentId, checklistId) => {
  invalidateStudentCache(studentId);
  return studentApi.post(`/${studentId}/eligibility-checklist/${checklistId}/evaluate-from-docs`);
};

export const deleteChecklist = (studentId, checklistId) => {
  invalidateStudentCache(studentId);
  return studentApi.delete(`/${studentId}/eligibility-checklist/${checklistId}`);
};

export const exportPdf = (studentId, checklistId) =>
  studentApi.get(`/${studentId}/eligibility-checklist/${checklistId}/export-pdf`, { responseType: 'blob' });

export const exportExcel = (studentId, checklistId) =>
  studentApi.get(`/${studentId}/eligibility-checklist/${checklistId}/export-excel`, { responseType: 'blob' });
